import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import TableContainer from "components/Common/TableContainer";
import CallExecutivePipelineBar from "components/Common/CallExecutivePipelineBar";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import { submitCallExecutiveNotOnAiFilterData, submitCallExecutiveQuickUpdate } from "model/CallModel";
import { useCallExecutiveContext } from "libs/context/CallExecutiveContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import VideoInterviewLinkModal from "pages/AllCommonModal/VideoInterviewLinkModal/video-interview-link-modal";
import Loaders from "components/Common/Loaders";
import { toast } from "react-toastify";
import {
  DEFAULT_PAGE_SIZE,
  appendCallExecutiveQuickUpdateColumnIfMissing,
  buildCallExecutiveTableColumnsFromMetadata,
  mapApiMetadataTableColumns,
  resolveCallExecutiveQuickUpdateCandidateClientId,
  resolveCallExecutiveQuickUpdateRowValue,
  urlEndPoint,
} from "helpers/GeneralHelper";
import {
  getAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  isMetadataAsyncSelectFilterDefinition,
  mergeModalSubmitIntoListFilter,
} from "helpers/metadataFilterHelpers";
import { useMetadataDrivenFilters } from "hooks/useMetadataDrivenFilters";
import { useCallExecutiveAppliedFilter } from "hooks/useCallExecutiveAppliedFilter";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";
import { resolveServerPageSizeMeta } from "components/Common/ServerListPagination";


const extractList = (response) => {
  if (response == null) return [];
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.records)) return raw.records;
  if (raw && Array.isArray(raw.data)) return raw.data;
  return [];
};

const getPaginationEnvelope = (response) => {
  const raw = response?.data;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
  return {};
};

const buildPaginationMeta = (envelope, payload, recordsLength) => ({
  pageNumber: Number(envelope?.pageNumber) || Number(payload?.pageNumber) || 1,
  pageSize: resolveServerPageSizeMeta(
    envelope?.pageSize,
    payload?.pageSize,
    DEFAULT_PAGE_SIZE
  ),
  totalPages: Number(envelope?.totalPages) || 0,
  totalRecords: Number(envelope?.totalRecords) || recordsLength,
});

const mapNotOnAiRows = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    ...item,
    id: item?.id ?? item?.Id ?? item?._id,
    profileStatusHtml:
      item?.profileStatusHtml ?? item?.ProfileStatusHtml ?? "",
    orderStatusHtml: item?.orderStatusHtml ?? item?.OrderStatusHtml ?? "",
    profileOrderStatus:
      item?.profileOrderStatus ?? item?.ProfileOrderStatus ?? "",
    profileAndOrderStatus:
      item?.profileAndOrderStatus ?? item?.ProfileAndOrderStatus ?? "",
    profileAndOrderStatusHtml:
      item?.profileAndOrderStatusHtml ?? item?.ProfileAndOrderStatusHtml ?? "",
  }));

const normalizeCallExecutiveFilterPayload = (raw = {}) => {
  const out = { ...raw };
  Object.keys(out).forEach((key) => {
    if (isAsyncFilterLabelFieldKey(key)) {
      delete out[key];
      return;
    }
    const v = out[key];
    if (v === "" || v === undefined) out[key] = null;
  });
  return out;
};

const NotOnAI = () => {
  const {
    fetchCallExecutiveNotOnAiContextData,
    callExecutiveNotOnAiContextData,
  } = useCallExecutiveContext() || {};
  const { pageSize: settingsPageSize, settingsReady } =
    useCommonTableSettingsPageSize("CallExecutiveNotOnAi");
  const initialFilterFetchedRef = useRef(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetadata] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [commonResetFilter, setCommonResetFilter] = useState(false);
  const [videoInterviewModalOpen, setVideoInterviewModalOpen] = useState(false);
  const [videoInterviewRow, setVideoInterviewRow] = useState(null);
  const [callExecutiveListFilter, setCallExecutiveListFilter] = useState({});
  const {
    initialLoadDoneRef,
    commitDraftAsApplied,
    resolveFilterBase,
    setAppliedFilter,
  } = useCallExecutiveAppliedFilter();
  const [pipelineStageKey, setPipelineStageKey] = useState(null);
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const filteredData = useMemo(() => {
    if (!searchQuery?.trim()) return tableData;

    return tableData?.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [tableData, searchQuery]);

  const {
    shouldShowTableHeaderFilterAndResetControls,
    shouldRenderMetadataToolbarFilterRow,
    activeModalFilterCount,
    modalFilterData: callExecutiveNotOnAiFilterModalData,
    metadataToolbarFilterDefinitions,
    metadataFiltersAll,
    usesMetadataDrivenFilterUi,
    buildBaselineEmptyListFilter,
    toolbarDropdownReactSelectOptionsByFieldName,
  } = useMetadataDrivenFilters(
    callExecutiveNotOnAiContextData?.filters,
    callExecutiveListFilter,
    "CallExecutiveNotOnAi"
  );

  useEffect(() => {
    const metadataFilters = callExecutiveNotOnAiContextData?.filters;
    if (!Array.isArray(metadataFilters) || !metadataFilters.length) return;
    setCallExecutiveListFilter((previousFilterState) => {
      const mergedFilterState = { ...previousFilterState };
      let mergedNewKeysIntoState = false;
      metadataFilters.forEach((filterDefinitionRow) => {
        const fieldKey = filterDefinitionRow?.field;
        if (!fieldKey) return;
        if (!(fieldKey in mergedFilterState)) {
          mergedFilterState[fieldKey] =
            filterDefinitionRow?.type === "date" ? null : "";
          mergedNewKeysIntoState = true;
        }
        if (isMetadataAsyncSelectFilterDefinition(filterDefinitionRow)) {
          const asyncLabelFieldKey = getAsyncFilterLabelFieldKey(fieldKey);
          if (!(asyncLabelFieldKey in mergedFilterState)) {
            mergedFilterState[asyncLabelFieldKey] = "";
            mergedNewKeysIntoState = true;
          }
        }
      });
      return mergedNewKeysIntoState ? mergedFilterState : previousFilterState;
    });
  }, [callExecutiveNotOnAiContextData?.filters]);

  const loadList = useCallback(
    async (overrides = {}, filterOverride) => {
      const filterBase = resolveFilterBase(filterOverride);
      setLoading(true);
      try {
        const payload = normalizeCallExecutiveFilterPayload({
          pageNumber: 1,
          pageSize: DEFAULT_PAGE_SIZE,
          ...filterBase,
          ...(pipelineStageKey ? { pipelineStage: pipelineStageKey } : {}),
          ...overrides,
        });
        const response = await submitCallExecutiveNotOnAiFilterData(payload);
        const list = extractList(response);
        const records = Array.isArray(list) ? list : [];
        setTableData(mapNotOnAiRows(records));
        const envelope = getPaginationEnvelope(response);
        setPaginationMeta(buildPaginationMeta(envelope, payload, records.length));
      } finally {
        setLoading(false);
      }
    },
    [pipelineStageKey, resolveFilterBase]
  );

  const loadListRef = useRef(loadList);
  loadListRef.current = loadList;

  useEffect(() => {
    fetchCallExecutiveNotOnAiContextData?.();
  }, [fetchCallExecutiveNotOnAiContextData]);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    initialLoadDoneRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setPaginationMeta((prev) => ({ ...prev, pageSize }));
    setAppliedFilter({});
    loadListRef.current({ pageNumber: 1, pageSize }, {});
  }, [settingsReady, settingsPageSize, setAppliedFilter, initialLoadDoneRef]);

  useEffect(() => {
    const columnsFromMetadata = mapApiMetadataTableColumns(
      callExecutiveNotOnAiContextData?.table?.columns
    );
    const mergedCallExecutiveColumns = appendCallExecutiveQuickUpdateColumnIfMissing(
      buildCallExecutiveTableColumnsFromMetadata(columnsFromMetadata)
    );
    setTableMetadata(
      mergedCallExecutiveColumns.length > 0
        ? mergedCallExecutiveColumns
        : columnsFromMetadata
    );
  }, [callExecutiveNotOnAiContextData]);

  const handleFilterReset = useCallback(async () => {
    const emptyFilter = buildBaselineEmptyListFilter();
    setCallExecutiveListFilter(emptyFilter);
    setAppliedFilter(emptyFilter);
    setPipelineStageKey(null);
    setCommonFilterModalOpen(false);
    await loadList(
      {
        pageNumber: 1,
        pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
      },
      emptyFilter
    );
  }, [buildBaselineEmptyListFilter, loadList, paginationMeta.pageSize, setAppliedFilter]);

  useEffect(() => {
    if (!commonResetFilter) return;
    const run = async () => {
      await handleFilterReset();
      setCommonResetFilter(false);
    };
    run();
  }, [commonResetFilter, handleFilterReset]);

  const handleFilterSubmit = async (formData = {}) => {
    const mergedFilter = mergeModalSubmitIntoListFilter(
      callExecutiveListFilter,
      callExecutiveNotOnAiFilterModalData,
      formData
    );
    setCallExecutiveListFilter(mergedFilter);
    setCommonFilterModalOpen(false);
    await loadList(
      {
        pageNumber: 1,
        pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
      },
      mergedFilter
    );
  };

  const handleToolbarTextFilterInputChange = (fieldKey, nextValue) => {
    setCallExecutiveListFilter((previousFilterState) => ({
      ...previousFilterState,
      [fieldKey]: nextValue ?? "",
    }));
  };

  const handleToolbarReactSelectFilterChange = (fieldKey, selectedOption) => {
    setCallExecutiveListFilter((previousFilterState) => ({
      ...previousFilterState,
      [fieldKey]: selectedOption?.value ?? "",
      [getAsyncFilterLabelFieldKey(fieldKey)]: selectedOption?.label ?? "",
    }));
  };

  const handleUpperToolbarFilterSearch = async () => {
    commitDraftAsApplied(callExecutiveListFilter);
    await loadList({
      pageNumber: 1,
      pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
    });
  };

  const handleCallExecutiveCall = (row) => {
    const candidateId = String(row?.id ?? "").trim();
    if (!candidateId) {
      toast.error("Candidate id is missing");
      return;
    }
    window.open(`/${urlEndPoint.callDetails}/${candidateId}`, "_blank", "noopener,noreferrer");
  };

  const handleCallExecutiveVideoInterview = (row) => {
    setVideoInterviewRow(row && typeof row === "object" ? { ...row } : {});
    setVideoInterviewModalOpen(true);
  };

  const effectiveColumns =
    tableMetaData?.length > 0 ? tableMetaData : undefined;

  const handleCallExecutiveQuickUpdateBlur = useCallback(
    async (rowRecord, nextValue, fieldAccessor) => {
      const candidateClientId = rowRecord?.id
        // resolveCallExecutiveQuickUpdateCandidateClientId(rowRecord);
      if (!candidateClientId) {
        toast.error("Candidate client id is missing");
        return;
      }

      const previousValue = resolveCallExecutiveQuickUpdateRowValue(
        rowRecord,
        fieldAccessor
      ).trim();
      const nextTrimmed = String(nextValue ?? "").trim();
      if (previousValue === nextTrimmed) return;

      const response = await submitCallExecutiveQuickUpdate(
        candidateClientId,
        nextTrimmed
      );
      if (response == null || response?.success === false) {
        if (response?.message) toast.error(response.message);
        return;
      }

      const fieldKey = fieldAccessor || "callQuickUpdate";
      setTableData((previousRows) =>
        previousRows.map((row) => {
          if (
            resolveCallExecutiveQuickUpdateCandidateClientId(row) !==
            candidateClientId
          ) {
            return row;
          }
          return { ...row, [fieldKey]: nextTrimmed };
        })
      );

      if (response?.message) {
        toast.success(response.message);
      }
    },
    []
  );

  const handlePipelineTabChange = async (nextKey) => {
    setPipelineStageKey(nextKey);
    setLoading(true);
    try {
      const payload = normalizeCallExecutiveFilterPayload({
        pageNumber: 1,
        pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
        ...resolveFilterBase(),
        ...(nextKey ? { pipelineStage: nextKey } : {}),
        ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
      });
      const response = await submitCallExecutiveNotOnAiFilterData(payload);
      const list = extractList(response);
      const records = Array.isArray(list) ? list : [];
      setTableData(mapNotOnAiRows(records));
      const envelope = getPaginationEnvelope(response);
      setPaginationMeta(buildPaginationMeta(envelope, payload, records.length));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (page) => {
    await loadList({
      pageNumber: page,
      pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
    });
  };

  const handleRowsPerPageChange = async (rows) => {
    await loadList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <React.Fragment>
      <div className="page-content call-exec-schedule-page">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <h5 className="fw-bold mb-0">Not On AI</h5>
              </div>
              <hr />

              {shouldRenderMetadataToolbarFilterRow ? (
                <MetadataFilterToolbar
                  toolbarDefinitions={metadataToolbarFilterDefinitions}
                  listFilter={callExecutiveListFilter}
                  onTextChange={handleToolbarTextFilterInputChange}
                  onSelectChange={handleToolbarReactSelectFilterChange}
                  onSearch={handleUpperToolbarFilterSearch}
                  onClear={handleFilterReset}
                  showClearWhenNoModalFilters={
                    !shouldShowTableHeaderFilterAndResetControls
                  }
                  toolbarDropdownOptionsByFieldName={
                    toolbarDropdownReactSelectOptionsByFieldName
                  }
                  layoutPreset="callExecutive"
                  pageName="CallExecutiveNotOnAi"
                />
              ) : null}

              <CallExecutivePipelineBar
                activeKey={pipelineStageKey}
                onTabChange={handlePipelineTabChange}
              />

              {/* <Row className="align-items-center g-2 mb-4 mt-1">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />

                    <Button
                      className="search-icon-button"
                      type="button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row> */}

              {loading ? (
                <Loaders />
              ) : effectiveColumns?.length ? (
                <TableContainer
                  columns={effectiveColumns}
                  data={filteredData}
                  isActionRequired
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta.pageNumber}
                  rowsPerPage={paginationMeta.pageSize}
                  totalPages={paginationMeta.totalPages}
                  totalRecords={paginationMeta.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  setCommonResetFilter={setCommonResetFilter}
                  allCommonFilter={shouldShowTableHeaderFilterAndResetControls}
                  activeFilterCount={activeModalFilterCount}
                  pageName="CallExecutiveNotOnAi"
                  onCallExecutiveCall={handleCallExecutiveCall}
                  onCallExecutiveVideoInterview={handleCallExecutiveVideoInterview}
                  onCallExecutiveQuickUpdateBlur={handleCallExecutiveQuickUpdateBlur}
                />
              ) : (
                <div className="text-muted text-center py-5">Unable to load table columns.</div>
              )}
            </CardBody>
          </Card>

          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            pageName="CallExecutiveNotOnAi"
            modalTitle="Not On AI"
            handleSubmit={handleFilterSubmit}
            filterData={callExecutiveNotOnAiFilterModalData}
            metadataFilterDefinitions={metadataFiltersAll}
            enableFilterToolbarCustomization={usesMetadataDrivenFilterUi}
            commonResetFilter={commonResetFilter}
            setCommonResetFilter={setCommonResetFilter}
          />

          <VideoInterviewLinkModal
            isOpen={videoInterviewModalOpen}
            onClose={() => {
              setVideoInterviewModalOpen(false);
              setVideoInterviewRow(null);
            }}
            row={videoInterviewRow || {}}
            onSuccess={() =>
              loadList({
                pageNumber: paginationMeta.pageNumber,
                pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
              })
            }
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

NotOnAI.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(NotOnAI);
